import {FC, useState, useCallback} from "react";
import {makeStyles} from "tss-react/mui";
import {
	Badge,
	Box,
	Breadcrumbs,
	Button,
	Divider,
	LinearProgress,
	Menu,
	MenuItem,
	Typography
} from "@mui/material";
import {Link, useParams} from "react-router-dom";
import {
	useGetAnnouncement,
	usePublishAnnouncement,
	useDraftAnnouncement
} from "@/app2/shared/data";
import {
	App2NotificationContext,
	App2NotificationSeverity
} from "@/app2/shared/contexts";
import {useContext} from "react";
import {PublishedAudienceLockedAlert} from "@/pages/ManagerView/Pages/Annoucements/PublishedAudienceLockedAlert";
import {AnnouncementUnseenByListModal} from "./index";
import {
	formatAnnouncementDeliveryDateTime,
	getAnnouncementAdminDisplayStatus
} from "./announcementAdminStatus";
import {AnnouncementPreview} from "@/pages/HomePage/Components/AnnouncementPreview";
import ConfirmationDialog from "@/components/common/dialogs/ConfirmationDialog";
import {IAnnouncement} from "@/types/announcement";
import {IUserEntity} from "@buildwitt/shapeshift-types/entities";

const useStyles = makeStyles()((theme) => ({
	adminAnnouncementLandingPage: {
		marginLeft: "auto",
		marginRight: "auto"
	},
	breadcrumbs: {
		marginBottom: "25px"
	},
	adminBanner: {
		position: "relative",
		display: "flex",
		flexDirection: "column",
		width: "100%",
		height: "auto",
		borderRadius: "5px",
		overflow: "hidden",
		marginBottom: "12px"
	},
	adminBannerTitle: {
		position: "relative",
		fontSize: "34px",
		lineHeight: "1.1",
		fontWeight: "600",
		letterSpacing: "0.25px",
		alignSelf: "end",
		margin: "12px 0 0",
		zIndex: 2
	},
	adminAnnouncementButton: {
		marginTop: "-2px"
	},
	contentBox: {
		marginTop: theme.spacing(3),
		padding: theme.spacing(3.5),
		backgroundColor: theme.palette.background.paper,
		border: 0,
		borderRadius: (theme as { borderRadius?: { small?: number } }).borderRadius?.small ?? 8
	},
	titleRow: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	adminAnnouncementLabel: {},
	adminBadge: {
		padding: theme.spacing(0.25, 1.25, 0.35),
		marginBottom: theme.spacing(0.5)
	},
	deactivateBadge: {
		padding: theme.spacing(0.25, 1.25, 0.35),
		marginBottom: theme.spacing(0.5),
		background: theme.palette.error.dark,
		"&:hover": {background: theme.palette.error.dark},
		border: `1px solid ${theme.palette.error.dark}`
	},
	editBadge: {
		padding: theme.spacing(0.25, 1.25, 0.35),
		marginBottom: theme.spacing(0.5)
	},
	announcementBox: {},
	adminTrainingFeedModule: {
		minWidth: "340px",
		maxWidth: "340px"
	},
	announcementList: {
		marginTop: theme.spacing(1),
		flexGrow: 1
	},
	announcementListItem: {
		fontSize: "20px"
	},
	announcementListLabel: {
		fontSize: "14px",
		marginTop: "8px",
		color: theme.palette.grey[600]
	}
}));

export const AdminAnnouncementLandingPage: FC = () => {
	const {announcementId} = useParams();
	const id = announcementId ? parseInt(announcementId, 10) : undefined;
	const {data: announcement, isLoading} = useGetAnnouncement(id);
	const publishMutation = usePublishAnnouncement();
	const draftMutation = useDraftAnnouncement();
	const {notify} = useContext(App2NotificationContext);
	const {classes} = useStyles();
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [unseenByListOpen, setUnseenByListOpen] = useState(false);
	const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
	const menuOpen = Boolean(menuAnchorEl);

	const handleSetLive = useCallback(() => {
		if (id == null) return;
		publishMutation.mutate(id, {
			onSuccess: () => {
				notify("Announcement set live successfully", "", App2NotificationSeverity.success, 5000);
			}
		});
		setMenuAnchorEl(null);
	}, [id, publishMutation, notify]);

	const handleDeactivate = useCallback(() => {
		if (id == null) return;
		draftMutation.mutate(id, {
			onSuccess: () => {
				notify("Announcement deactivated successfully", "", App2NotificationSeverity.success, 5000);
				setDeactivateDialogOpen(false);
			},
			onError: () => {
				notify("Failed to deactivate announcement", "", App2NotificationSeverity.error, 5000);
				setDeactivateDialogOpen(false);
			}
		});
		setMenuAnchorEl(null);
	}, [id, draftMutation, notify]);

	if (isLoading || !announcement) {
		return <LinearProgress />;
	}

	const status = getAnnouncementAdminDisplayStatus(announcement);
	const isLive = status === "live";
	const processing = status === "processing";
	const isInactive = status === "inactive";
	const isDraft = status === "draft";
	const isScheduled = status === "scheduled";
	const announcementSeenBySummaryText =
		"Seen by " +
		(isDraft
			? ""
			: announcement.seenBy + "/" + (announcement.unseenBy + announcement.seenBy));

	return (
		<>
			<Box className={classes.adminAnnouncementLandingPage}>
				<Breadcrumbs className={classes.breadcrumbs} separator="/" aria-label="breadcrumb">
					<Link to="..">
						<Typography variant="body1">Announcements</Typography>
					</Link>
					<Link to={`${announcementId}`}>
						<Typography variant="body1">{announcement.title}</Typography>
					</Link>
				</Breadcrumbs>
				<Box className={classes.adminBanner} />
				<Box className={classes.contentBox}>
					<Box className={classes.titleRow}>
						<Typography variant="h4" className={classes.adminAnnouncementLabel}>
							Announcement Details
						</Typography>
						<Box sx={{display: "flex"}} gap={2}>
							{isLive && (
								<Button
									variant="contained"
									className={classes.deactivateBadge}
									onClick={() => setDeactivateDialogOpen(true)}
									color="error"
								>
									DEACTIVATE
								</Button>
							)}
							{!isScheduled && (
								<Link to="edit">
									<Button variant="contained" className={classes.editBadge}>
										Edit
									</Button>
								</Link>
							)}
						</Box>
						{isScheduled && (
							<>
								<Button
									className={classes.adminAnnouncementButton}
									variant="contained"
									id="announcement-action-menu-button"
									aria-controls={
										menuOpen ? "announcement-action-menu-button" : undefined
									}
									aria-haspopup="true"
									aria-expanded={menuOpen ? "true" : undefined}
									onClick={(e) => setMenuAnchorEl(e.currentTarget)}
								>
									Actions
								</Button>
								<Menu
									id="action-menu"
									anchorEl={menuAnchorEl}
									open={menuOpen}
									onClose={() => setMenuAnchorEl(null)}
									MenuListProps={{"aria-labelledby": "basic-button"}}
								>
									<Link to="edit">
										<MenuItem>Edit</MenuItem>
									</Link>
									<MenuItem onClick={handleSetLive}>Set Live</MenuItem>
									<Divider />
									<MenuItem
										onClick={() => {
											setMenuAnchorEl(null);
											setDeactivateDialogOpen(true);
										}}
										sx={(t) => ({color: t.palette.error.main})}
									>
										Deactivate
									</MenuItem>
								</Menu>
							</>
						)}
					</Box>
					{announcement.delivered && <PublishedAudienceLockedAlert />}
					<Box className={classes.announcementBox} sx={{display: "flex"}} gap={5}>
						<Box width={"55%"} bgcolor={"background.paperElevation2"} borderRadius={"16px"} padding={"16px"} display={"flex"} flexDirection={"column"}>
							<AnnouncementPreview
								announcement={announcement as unknown as IAnnouncement}
								author={
									{
										firstname: announcement?.firstname,
										lastname: announcement?.lastname,
										profileImage: announcement?.profileImage ?? undefined,
										jobTitle: announcement?.jobTitle
									} as unknown as IUserEntity
								}
							/>
						</Box>
						<Box
							className={classes.announcementList}
							display="flex"
							flexDirection="column"
							gap={1}
						>
							<Divider />
							<Box>
								{isLive && (
									<Badge className={classes.adminBadge} color="success">
										Live
									</Badge>
								)}
								{isScheduled && (
									<Badge className={classes.adminBadge} color="warning">
										Scheduled
									</Badge>
								)}
								{processing && (
									<Badge className={classes.adminBadge} color="warning">
										Processing
									</Badge>
								)}
								{isInactive && (
									<Badge className={classes.adminBadge} color="info">
										Inactive
									</Badge>
								)}
								{isDraft && (
									<Badge className={classes.adminBadge} color="info">
										Draft
									</Badge>
								)}
								<Typography className={classes.announcementListLabel}>
									Status
								</Typography>
							</Box>
							<Divider />
							<Box>
								<Typography className={classes.announcementListItem} variant="h5">
									{announcement.author ?? "—"}
								</Typography>
								<Typography className={classes.announcementListLabel}>
									Author
								</Typography>
							</Box>
							<Divider />
							<Box>
								<Typography className={classes.announcementListItem} variant="h5">
									{formatAnnouncementDeliveryDateTime(announcement) || "—"}
								</Typography>
								<Typography className={classes.announcementListLabel}>
									Delivery date & time
								</Typography>
							</Box>
							<Divider />

							<Box display="flex" justifyContent="space-between">
								<Typography
									className={classes.announcementListItem}
									variant="h5"
								>
									{announcementSeenBySummaryText}
								</Typography>
								<Button
									disabled={isDraft}
									onClick={() => setUnseenByListOpen(true)}
									variant="contained"
								>
									VIEW LIST
								</Button>
							</Box>
							{processing && (
								<Box>
									<Typography
										className={classes.announcementListItem}
										variant="h5"
									>
										Processing
									</Typography>
								</Box>
							)}
							<Divider />
						</Box>
					</Box>
				</Box>
			</Box>
			{unseenByListOpen && id !== undefined && (
				<AnnouncementUnseenByListModal
					announcementId={id}
					title={announcementSeenBySummaryText}
					onClose={() => setUnseenByListOpen(false)}
				/>
			)}
			<ConfirmationDialog
				open={deactivateDialogOpen}
				onClose={() => setDeactivateDialogOpen(false)}
				onCancel={() => setDeactivateDialogOpen(false)}
				onConfirm={handleDeactivate}
				title="Are you sure you want to deactivate this announcement?"
				content="Deactivating this announcement will remove it from employee feeds. Existing read data will remain available. You can reactivate it at any time."
				testIdPrefix="deactivate-announcement-confirmation"
				confirmButtonTitle="Deactivate"
				confirmButtonColor="error"
			/>
		</>
	);
};
